import { FC, useState, useCallback, useRef } from 'react'
import { Link } from 'react-router-dom'
import MavNavBar, { MAV_NAV_CSS } from '../components/MavNavBar'

type OutputMode = 'launch' | 'direct' | 'json'

interface CasePayload {
  ID: string
  Name: string
  StorageAccount: string
  StorageContainer: string
  DataPath?: string
}

interface SessionPayload {
  Name?: string
  Description?: string
  LocalStoragePath?: string
  NetworkStoragePath?: string
  Cases: CasePayload[]
  SliceAxis?: string
  SlicePosition?: number
  SliceVariable?: string
  SurfaceVariable?: string
  View?: string
  IsLICEnabled?: boolean
  IsDelta?: boolean
}

interface LaunchOutputItem {
  kind: 'launch' | 'direct'
  url: string
  label: string
  id: number
}

interface JsonOutputItem {
  kind: 'json'
  formatted: string
  minified: string
  urlEncoded: string
  id: number
}

type OutputItem = LaunchOutputItem | JsonOutputItem

const MAVAeroWebLauncher: FC = () => {
  const [account, setAccount] = useState('dlaerocfdcupeus2')
  const [container, setContainer] = useState('cfd-cases')
  const [sessionName, setSessionName] = useState('')
  const [sessionDescription, setSessionDescription] = useState('')
  const [localStoragePath, setLocalStoragePath] = useState('')
  const [networkStoragePath, setNetworkStoragePath] = useState('')
  const [ids, setIds] = useState('')
  const [pathPrefix, setPathPrefix] = useState('')
  const [sliceAxis, setSliceAxis] = useState('')
  const [slicePosition, setSlicePosition] = useState('')
  const [sliceVariable, setSliceVariable] = useState('')
  const [surfaceVariable, setSurfaceVariable] = useState('')
  const [view, setView] = useState('')
  const [isLICEnabled, setIsLICEnabled] = useState(false)
  const [isDelta, setIsDelta] = useState(false)
  const [outputMode, setOutputMode] = useState<OutputMode>('launch')

  const [accountError, setAccountError] = useState(false)
  const [containerError, setContainerError] = useState(false)
  const [statusMsg, setStatusMsg] = useState('')

  const [outputItems, setOutputItems] = useState<OutputItem[]>([])
  const [copiedKeys, setCopiedKeys] = useState<Record<string, boolean>>({})

  const nextId = useRef(0)

  const copyToClipboard = useCallback(async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopiedKeys(prev => ({ ...prev, [key]: true }))
      setTimeout(() => setCopiedKeys(prev => ({ ...prev, [key]: false })), 1400)
    } catch {}
  }, [])

  const renderCopyBtn = (text: string, key: string) => (
    <button
      type="button"
      className="wl-copy-btn"
      onClick={() => copyToClipboard(text, key)}
      style={copiedKeys[key] ? { background: '#16a34a', color: '#fff' } : undefined}>
      {copiedKeys[key] ? 'COPIED' : 'COPY'}
    </button>
  )

  const handleAutoGenerate = useCallback(() => {
    const parsedIds = Array.from(
      new Set(ids.split(/[\n,\s]+/).map(s => s.trim()).filter(Boolean))
    )
    if (!parsedIds.length) return
    if (parsedIds.length === 1) {
      setSessionName(`Session ${parsedIds[0]}`)
      setSessionDescription(`Single case ${parsedIds[0]}`)
    } else if (parsedIds.length === 2) {
      setSessionName(`Session ${parsedIds[0]}-${parsedIds[1]}`)
      setSessionDescription(`Comparing ${parsedIds[1]} against baseline ${parsedIds[0]}`)
    } else {
      const comparisons = parsedIds.slice(1).join(', ')
      setSessionName(`Session ${parsedIds[0]}-${parsedIds[parsedIds.length - 1]}`)
      setSessionDescription(`Comparing ${comparisons} against baseline ${parsedIds[0]}`)
    }
  }, [ids])

  const handleGo = useCallback(() => {
    setStatusMsg('')
    const okAcc = account.trim() !== ''
    const okCon = container.trim() !== ''
    setAccountError(!okAcc)
    setContainerError(!okCon)
    if (!okAcc || !okCon) return

    const parsedIds = Array.from(
      new Set(ids.split(/[\n,\s]+/).map(s => s.trim()).filter(Boolean))
    )
    if (!parsedIds.length) {
      setStatusMsg('No IDs provided.')
      return
    }

    const acc = account.trim()
    const cont = container.trim()
    const prefix = pathPrefix.trim()

    const payload: SessionPayload = {
      Cases: parsedIds.map((id, i) => {
        const caseName =
          i === 0 ? 'Baseline' : parsedIds.length === 2 ? 'Comparison' : `Comparison ${i}`
        const caseObj: CasePayload = { ID: id, Name: caseName, StorageAccount: acc, StorageContainer: cont }
        if (prefix) {
          caseObj.DataPath = `https://${acc}.blob.core.windows.net/${cont}/${prefix}/cases/${id}/Results/MAV_${id}`
        }
        return caseObj
      })
    }
    if (sessionName.trim()) payload.Name = sessionName.trim()
    if (sessionDescription.trim()) payload.Description = sessionDescription.trim()
    if (localStoragePath.trim()) payload.LocalStoragePath = localStoragePath.trim()
    if (networkStoragePath.trim()) payload.NetworkStoragePath = networkStoragePath.trim()
    if (sliceAxis) payload.SliceAxis = sliceAxis
    if (slicePosition !== '') payload.SlicePosition = parseFloat(slicePosition)
    if (sliceVariable) payload.SliceVariable = sliceVariable
    if (surfaceVariable) payload.SurfaceVariable = surfaceVariable
    if (view) payload.View = view
    if (isLICEnabled) payload.IsLICEnabled = true
    if (isDelta) payload.IsDelta = true

    const jsonStr = JSON.stringify(payload)
    const itemId = ++nextId.current

    if (outputMode === 'json') {
      setOutputItems(prev => [
        {
          kind: 'json' as const,
          formatted: JSON.stringify(payload, null, 2),
          minified: jsonStr,
          urlEncoded: encodeURIComponent(jsonStr),
          id: itemId
        },
        ...prev
      ])
      setStatusMsg('JSON generated.')
    } else {
      const url =
        outputMode === 'direct'
          ? `http://localhost:8342/mav/import/${encodeURIComponent(jsonStr)}`
          : `mav-launcher://${encodeURIComponent(jsonStr)}`
      const label =
        outputMode === 'direct'
          ? `Direct (${parsedIds.length} cases)`
          : `Launch (${parsedIds.length} cases)`
      setOutputItems(prev => [{ kind: outputMode, url, label, id: itemId }, ...prev])
      setStatusMsg(`Launching via ${outputMode} mode\u2026`)
      window.open(url, '_blank')
    }
  }, [account, container, ids, outputMode, pathPrefix, sessionName, sessionDescription, localStoragePath, networkStoragePath, sliceAxis, slicePosition, sliceVariable, surfaceVariable, view, isLICEnabled, isDelta])

  const handleRandomize = useCallback(() => {
    const axes = ['X', 'Y', 'Z', 'R']
    const sliceVars = ['Cp', 'CpT', 'HeatFlux', 'Helicity', 'QCriterion', 'Temperature', 'VelocityMagnitude', 'Vorticity_i', 'X_VR', 'Y_VR', 'Z_VR']
    const surfaceVars = ['Cp', 'CpT', 'CpX', 'CSF', 'HeatFlux', 'HeatTransferCoefficient', 'RMSdCp']
    const views = [
      'Back', 'Bottom', 'BottomDown', 'BottomFront', 'BottomRear', 'BottomUp', 'Front',
      'Left', 'LeftFront', 'LeftRear', 'Right', 'RightFront', 'RightRear',
      'Top', 'TopDown', 'TopFront', 'TopRear', 'TopUp',
      'Default', 'FrontRightQuarter', 'FrontLeftQuarter', 'BackLeftQuarter', 'BackRightQuarter'
    ]
    const pick = <T,>(arr: T[]) => arr[Math.floor(Math.random() * arr.length)]
    const pos = (Math.random() * 2 - 1).toFixed(3)
    setSliceAxis(pick(axes))
    setSlicePosition(pos)
    setSliceVariable(pick(sliceVars))
    setSurfaceVariable(pick(surfaceVars))
    setView(pick(views))
  }, [])

  return (
    <>
      <style>{`
        :root {
          --mav-blue: rgb(0, 114, 206);
          --mav-blue-dark: rgb(0, 90, 165);
          --mav-blue-light: rgba(0, 114, 206, 0.15);
          --mav-text: #e2e8f0;
          --mav-muted: #94a3b8;
          --mav-border: #2d3748;
        }

        ${MAV_NAV_CSS}

        /* ── PAGE WRAPPER ── */
        .mav-page-wrapper {
          width: calc(100% + 4rem);
          margin-left: -2rem;
          margin-right: -2rem;
          align-self: stretch;
        }
        body.dark .mav-nav { background: #06080c; }

        /* ── HERO ── */
        .wl-hero {
          background: linear-gradient(160deg, #0c1018 60%, #0a2042 100%);
          padding: 56px 48px 40px;
          text-align: center;
        }
        .wl-hero-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: var(--mav-blue-light);
          color: var(--mav-blue);
          font-size: .8rem;
          font-weight: 600;
          letter-spacing: .06em;
          text-transform: uppercase;
          padding: 4px 14px;
          border-radius: 20px;
          margin-bottom: 20px;
        }
        .wl-hero h1 {
          font-size: clamp(1.8rem, 3vw, 2.8rem);
          font-weight: 800;
          line-height: 1.15;
          margin: 0 0 12px;
          letter-spacing: -.02em;
          color: var(--mav-text);
        }
        .wl-hero h1 span { color: var(--mav-blue); }
        .wl-hero p {
          font-size: 1rem;
          color: var(--mav-muted);
          max-width: 520px;
          margin: 0 auto;
        }

        /* ── MAIN SECTION ── */
        .wl-section {
          background: #0c1018;
          padding: 48px 48px 64px;
        }
        .wl-section-inner {
          max-width: 1080px;
          margin: 0 auto;
        }

        /* ── FORM CARD ── */
        .wl-card {
          background: #1e293b;
          border: 1px solid var(--mav-border);
          border-radius: 12px;
          padding: 1.25rem 1.5rem 1.5rem;
        }
        .wl-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: .75rem 1.5rem;
          align-items: start;
        }
        .wl-field { display: flex; flex-direction: column; gap: .3rem; }
        .wl-span2 { grid-column: 1 / -1; }
        .wl-label {
          display: block;
          font-size: .82rem;
          font-weight: 600;
          color: var(--mav-text);
        }
        .wl-input {
          width: 100%;
          padding: .55rem .7rem;
          font: inherit;
          font-size: .9rem;
          border: 1px solid var(--mav-border);
          border-radius: 6px;
          background: #0c1018;
          color: var(--mav-text);
          transition: border-color .15s;
        }
        .wl-input:focus {
          outline: 2px solid rgba(0, 114, 206, 0.35);
          border-color: var(--mav-blue);
        }
        .wl-input::placeholder { color: var(--mav-muted); opacity: 1; }
        textarea.wl-input { resize: vertical; min-height: 80px; }
        textarea.wl-input.wl-ids { min-height: 2.4rem; height: 2.4rem; resize: vertical; }
        textarea.wl-input.wl-desc { min-height: 70px; }
        .wl-hint { font-size: .72rem; color: var(--mav-muted); margin-top: .15rem; }
        .wl-error { font-size: .75rem; color: #f87171; margin-top: .15rem; }
        .wl-actions {
          display: flex;
          gap: .6rem;
          align-items: center;
          flex-wrap: wrap;
          justify-content: flex-end;
          padding-top: .5rem;
        }
        .wl-mode-select {
          padding: .55rem .9rem;
          font: inherit;
          font-size: .9rem;
          font-weight: 700;
          border: 2px solid var(--mav-border);
          border-radius: 6px;
          background: #0c1018;
          color: var(--mav-text);
          cursor: pointer;
        }
        .wl-mode-select:focus {
          outline: 2px solid rgba(0, 114, 206, 0.35);
          border-color: var(--mav-blue);
        }
        .wl-btn-go {
          background: var(--mav-blue);
          color: #fff;
          border: 1px solid var(--mav-blue-dark);
          padding: .65rem 1.25rem;
          border-radius: 6px;
          font: inherit;
          font-weight: 600;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: .4rem;
          transition: background .15s;
        }
        .wl-btn-go:hover { background: var(--mav-blue-dark); }
        .wl-status { font-size: .72rem; color: var(--mav-muted); min-height: 1em; margin-top: .25rem; }

        /* ── INFO PANELS ── */
        .wl-panels {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 1rem;
          margin-top: 1.25rem;
        }
        .wl-panel {
          font-size: .8rem;
          line-height: 1.5;
          background: #1e293b;
          border: 1px solid var(--mav-border);
          border-radius: 10px;
          overflow: hidden;
        }
        .wl-panel summary {
          list-style: none;
          cursor: pointer;
          padding: .65rem 1rem;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: .45rem;
          background: #2d3748;
          user-select: none;
          color: var(--mav-text);
        }
        .wl-panel summary::-webkit-details-marker { display: none; }
        .wl-panel[open] summary { border-bottom: 1px solid var(--mav-border); }
        .wl-panel-content { padding: .85rem 1rem 1rem; color: var(--mav-text); }
        .wl-chevron { transition: transform .25s ease; font-size: .8rem; display: inline-block; }
        details[open] > summary .wl-chevron { transform: rotate(90deg); }
        .wl-dl {
          margin: 0;
          display: grid;
          grid-template-columns: 150px 1fr;
          row-gap: .55rem;
          column-gap: 1rem;
        }
        .wl-dt { font-weight: 600; }
        .wl-dd { margin: 0; color: var(--mav-muted); }
        .wl-dd code {
          font-size: .7rem;
          background: #0a1628;
          color: var(--mav-text);
          padding: .12rem .3rem;
          border-radius: 4px;
        }

        /* ── OUTPUT ── */
        .wl-output-wrap { margin-top: 1.75rem; display: flex; flex-direction: column; gap: .75rem; }
        .wl-output-hr { border: none; border-top: 1px solid var(--mav-border); margin: 0 0 .5rem; }

        /* ── LAUNCH / DIRECT ITEM ── */
        .wl-gen-item {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: .65rem;
          padding: .65rem .85rem;
          border: 1px solid var(--mav-border);
          border-radius: 10px;
          background: #1e293b;
        }
        .wl-launch-btn {
          background: #16a34a;
          color: #fff;
          border: 1px solid #15803d;
          padding: .5rem .9rem;
          border-radius: 6px;
          font: inherit;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          transition: background .15s;
        }
        .wl-launch-btn:hover { background: #15803d; }
        .wl-direct-btn {
          background: var(--mav-blue);
          color: #fff;
          border: 1px solid var(--mav-blue-dark);
          padding: .5rem .9rem;
          border-radius: 6px;
          font: inherit;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          transition: background .15s;
        }
        .wl-direct-btn:hover { background: var(--mav-blue-dark); }
        .wl-url-code {
          font-size: .73rem;
          font-family: ui-monospace, Consolas, "Courier New", monospace;
          background: #0a1628;
          color: var(--mav-text);
          padding: .35rem .55rem;
          border-radius: 4px;
          flex: 1 1 auto;
          overflow: auto;
          white-space: nowrap;
          max-width: 100%;
        }
        .wl-copy-btn {
          background: #2d3748;
          color: var(--mav-text);
          padding: .38rem .65rem;
          border-radius: 5px;
          font: inherit;
          font-size: .65rem;
          font-weight: 600;
          letter-spacing: .5px;
          border: 1px solid #3d4f63;
          cursor: pointer;
          white-space: nowrap;
          transition: background .15s, color .15s;
        }
        .wl-copy-btn:focus { outline: 2px solid rgba(0, 114, 206, 0.35); }

        /* ── JSON CARD ── */
        .wl-json-card { display: flex; flex-direction: column; gap: .75rem; }
        .wl-json-section {
          border: 1px solid var(--mav-border);
          border-radius: 10px;
          overflow: hidden;
          background: #1e293b;
        }
        .wl-json-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: .45rem .8rem;
          background: #2d3748;
          border-bottom: 1px solid var(--mav-border);
          font-weight: 600;
          font-size: .78rem;
          color: var(--mav-text);
        }
        .wl-json-pre {
          margin: 0;
          padding: .75rem .9rem;
          font-size: .74rem;
          font-family: ui-monospace, Consolas, "Courier New", monospace;
          white-space: pre-wrap;
          word-break: break-all;
          background: #0a1628;
          color: var(--mav-text);
          overflow-x: auto;
        }

        /* ── LABEL ROW + AUTO-GENERATE ── */
        .wl-label-row {
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .wl-label-row .wl-label { margin-bottom: 0; }
        .wl-btn-autogen {
          background: #fff;
          color: var(--mav-blue);
          border: 1px solid var(--mav-blue);
          padding: .28rem .75rem;
          border-radius: 5px;
          font: inherit;
          font-size: .7rem;
          font-weight: 600;
          cursor: pointer;
          transition: background .15s, color .15s;
          flex-shrink: 0;
          box-shadow: 0 1px 3px rgba(0,0,0,.15);
        }
        .wl-btn-autogen:hover { background: var(--mav-blue); color: #fff; }

        /* ── FOOTER ── */
        .mav-footer {
          background: #080d1a;
          border-top: 1px solid var(--mav-border);
          padding: 40px 48px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 16px;
        }
        .mav-footer-brand { font-weight: 700; color: var(--mav-blue); font-size: 1rem; }
        .mav-footer p { font-size: .82rem; color: #64748b; }
        .mav-footer-links { display: flex; gap: 24px; }
        .mav-footer-links a { font-size: .82rem; color: var(--mav-muted); text-decoration: none; }
        .mav-footer-links a:hover { color: var(--mav-blue); }

        /* ── RESPONSIVE ── */
        @media (max-width: 768px) {
          .wl-hero { padding: 40px 24px 28px; }
          .wl-section { padding: 32px 24px 48px; }
          .mav-footer { padding: 32px 24px; }
          .mav-nav { padding: 0 20px; }
        }
        @media (max-width: 640px) {
          .wl-form-grid { grid-template-columns: 1fr; }
          .wl-span2 { grid-column: 1; }
          .wl-panels { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="mav-page-wrapper">
        <MavNavBar />

        {/* HERO */}
        <div className="wl-hero">
          <div className="wl-hero-badge">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="rgb(0,114,206)">
              <circle cx="5" cy="5" r="5" />
            </svg>
            Web Launcher
          </div>
          <h1>
            MAV-Aero <span>Web Launcher</span>
          </h1>
          <p>
            Generate deep-link URLs to open MAV-Aero with pre-loaded case sessions directly from
            the browser.
          </p>
        </div>

        {/* MAIN CONTENT */}
        <div className="wl-section">
          <div className="wl-section-inner">

            {/* FORM */}
            <div className="wl-card">
              <div className="wl-form-grid">

                {/* IDs */}
                <div className="wl-field wl-span2">
                  <label className="wl-label" htmlFor="wl-ids">
                    IDs comma or space separated - 1st case is baseline
                  </label>
                  <textarea
                    id="wl-ids"
                    className="wl-input wl-ids"
                    placeholder={'id1, id2 id3'}
                    value={ids}
                    onChange={e => setIds(e.target.value)}
                  />
                  <span className="wl-hint">
                    Separators accepted: comma, space, newline. Duplicates removed, order preserved.
                    First ID is named &ldquo;Baseline&rdquo;; subsequent IDs are named
                    &ldquo;Comparison&rdquo; (or &ldquo;Comparison 2&rdquo;,
                    &ldquo;Comparison 3&rdquo;, &hellip;).
                  </span>
                </div>

                {/* ── Case & Storage Details ── */}
                <div className="wl-span2" style={{ display: 'flex', alignItems: 'center', borderTop: '1px solid var(--mav-border)', paddingTop: '.75rem', marginTop: '.25rem' }}>
                  <span className="wl-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>Case &amp; Storage Details</span>
                </div>

                {/* Storage Account */}
                <div className="wl-field">
                  <label className="wl-label" htmlFor="wl-account">
                    Storage Account Name
                  </label>
                  <input
                    id="wl-account"
                    className="wl-input"
                    type="text"
                    autoComplete="off"
                    placeholder="e.g. mystorageacct"
                    value={account}
                    onChange={e => {
                      setAccount(e.target.value)
                      setAccountError(false)
                    }}
                  />
                  {accountError && <span className="wl-error">Required.</span>}
                </div>

                {/* Storage Container */}
                <div className="wl-field">
                  <label className="wl-label" htmlFor="wl-container">
                    Storage Container Name
                  </label>
                  <input
                    id="wl-container"
                    className="wl-input"
                    type="text"
                    autoComplete="off"
                    placeholder="e.g. cfd-cases"
                    value={container}
                    onChange={e => {
                      setContainer(e.target.value)
                      setContainerError(false)
                    }}
                  />
                  {containerError && <span className="wl-error">Required.</span>}
                </div>

                {/* Local Storage Path */}
                <div className="wl-field">
                  <label className="wl-label" htmlFor="wl-local-path">
                    Local Storage Path (optional)
                  </label>
                  <input
                    id="wl-local-path"
                    className="wl-input"
                    type="text"
                    placeholder="C:\MAV-Aero"
                    value={localStoragePath}
                    onChange={e => setLocalStoragePath(e.target.value)}
                  />
                </div>

                {/* Network Storage Path */}
                <div className="wl-field">
                  <label className="wl-label" htmlFor="wl-network-path">
                    Network Storage Path (optional)
                  </label>
                  <input
                    id="wl-network-path"
                    className="wl-input"
                    type="text"
                    placeholder="\\server\share\MAV-Aero"
                    value={networkStoragePath}
                    onChange={e => setNetworkStoragePath(e.target.value)}
                  />
                </div>

                {/* Path Prefix */}
                <div className="wl-field wl-span2">
                  <label className="wl-label" htmlFor="wl-path-prefix">
                    Data Path Prefix (optional)
                  </label>
                  <input
                    id="wl-path-prefix"
                    className="wl-input"
                    type="text"
                    autoComplete="off"
                    placeholder="e.g. NASCAR-Cup"
                    value={pathPrefix}
                    onChange={e => setPathPrefix(e.target.value)}
                  />
                  <span className="wl-hint">
                    Builds DataPath: https://&#123;account&#125;.blob.core.windows.net/&#123;container&#125;/&#123;prefix&#125;/cases/&#123;id&#125;/Results/MAV_&#123;id&#125;
                  </span>
                </div>

                {/* ── Session Details ── */}
                <div className="wl-span2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--mav-border)', paddingTop: '.75rem', marginTop: '.25rem' }}>
                  <span className="wl-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>Session Details</span>
                  <button
                    type="button"
                    className="wl-btn-autogen"
                    onClick={handleAutoGenerate}
                    title="Generate name and description from IDs">
                    ✨ Auto-Generate
                  </button>
                </div>

                {/* Session Name */}
                <div className="wl-field">
                  <label className="wl-label" htmlFor="wl-session-name">
                    Name (optional)
                  </label>
                  <input
                    id="wl-session-name"
                    className="wl-input"
                    type="text"
                    autoComplete="off"
                    placeholder="Session name"
                    value={sessionName}
                    onChange={e => setSessionName(e.target.value)}
                  />
                </div>

                {/* Session Description */}
                <div className="wl-field">
                  <label className="wl-label" htmlFor="wl-session-desc">
                    Description (optional)
                  </label>
                  <textarea
                    id="wl-session-desc"
                    className="wl-input wl-desc"
                    placeholder="Short description"
                    value={sessionDescription}
                    onChange={e => setSessionDescription(e.target.value)}
                  />
                </div>

                {/* ── Visualization Options ── */}
                <div className="wl-span2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--mav-border)', paddingTop: '.75rem', marginTop: '.25rem' }}>
                  <span className="wl-label" style={{ margin: 0, whiteSpace: 'nowrap' }}>Visualization Options</span>
                  <button
                    type="button"
                    className="wl-btn-autogen"
                    onClick={handleRandomize}
                    title="Randomly populate slice and view options"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '5px' }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="2" width="20" height="20" rx="3" ry="3"/>
                      <circle cx="8" cy="8" r="1.5" fill="currentColor"/>
                      <circle cx="16" cy="8" r="1.5" fill="currentColor"/>
                      <circle cx="8" cy="16" r="1.5" fill="currentColor"/>
                      <circle cx="16" cy="16" r="1.5" fill="currentColor"/>
                      <circle cx="12" cy="12" r="1.5" fill="currentColor"/>
                    </svg>
                    Randomize
                  </button>
                </div>

                {/* Slice Axis */}
                <div className="wl-field">
                  <label className="wl-label" htmlFor="wl-slice-axis">
                    Slice Axis
                  </label>
                  <select
                    id="wl-slice-axis"
                    className="wl-input"
                    value={sliceAxis}
                    onChange={e => setSliceAxis(e.target.value)}>
                    <option value="">-- None --</option>
                    <option value="X">X</option>
                    <option value="Y">Y</option>
                    <option value="Z">Z</option>
                    <option value="R">R (Radial / Cylindrical)</option>
                  </select>
                </div>

                {/* Slice Position */}
                <div className="wl-field">
                  <label className="wl-label" htmlFor="wl-slice-pos">
                    Slice Position (m)
                  </label>
                  <input
                    id="wl-slice-pos"
                    className="wl-input"
                    type="number"
                    step="any"
                    placeholder="0.0"
                    value={slicePosition}
                    onChange={e => setSlicePosition(e.target.value)}
                  />
                </div>

                {/* Slice Variable */}
                <div className="wl-field">
                  <label className="wl-label" htmlFor="wl-slice-var">
                    Slice Variable
                  </label>
                  <select
                    id="wl-slice-var"
                    className="wl-input"
                    value={sliceVariable}
                    onChange={e => setSliceVariable(e.target.value)}>
                    <option value="">-- None --</option>
                    <option value="Cp">Cp</option>
                    <option value="CpT">CpT</option>
                    <option value="HeatFlux">HeatFlux</option>
                    <option value="Helicity">Helicity</option>
                    <option value="QCriterion">QCriterion</option>
                    <option value="Temperature">Temperature</option>
                    <option value="VelocityMagnitude">VelocityMagnitude</option>
                    <option value="Vorticity_i">Vorticity_i</option>
                    <option value="X_VR">X_VR</option>
                    <option value="Y_VR">Y_VR</option>
                    <option value="Z_VR">Z_VR</option>
                  </select>
                </div>

                {/* Surface Variable */}
                <div className="wl-field">
                  <label className="wl-label" htmlFor="wl-surface-var">
                    Surface Variable
                  </label>
                  <select
                    id="wl-surface-var"
                    className="wl-input"
                    value={surfaceVariable}
                    onChange={e => setSurfaceVariable(e.target.value)}>
                    <option value="">-- None --</option>
                    <option value="Cp">Cp</option>
                    <option value="CpT">CpT</option>
                    <option value="CpX">CpX</option>
                    <option value="CSF">CSF</option>
                    <option value="HeatFlux">HeatFlux</option>
                    <option value="HeatTransferCoefficient">HeatTransferCoefficient</option>
                    <option value="RMSdCp">RMSdCp</option>
                  </select>
                </div>

                {/* View */}
                <div className="wl-field">
                  <label className="wl-label" htmlFor="wl-view">
                    View
                  </label>
                  <select
                    id="wl-view"
                    className="wl-input"
                    value={view}
                    onChange={e => setView(e.target.value)}>
                    <option value="">-- None --</option>
                    <optgroup label="2D">
                      <option value="Back">Back</option>
                      <option value="Bottom">Bottom</option>
                      <option value="BottomDown">BottomDown</option>
                      <option value="BottomFront">BottomFront</option>
                      <option value="BottomRear">BottomRear</option>
                      <option value="BottomUp">BottomUp</option>
                      <option value="Front">Front</option>
                      <option value="Left">Left</option>
                      <option value="LeftFront">LeftFront</option>
                      <option value="LeftRear">LeftRear</option>
                      <option value="Right">Right</option>
                      <option value="RightFront">RightFront</option>
                      <option value="RightRear">RightRear</option>
                      <option value="Top">Top</option>
                      <option value="TopDown">TopDown</option>
                      <option value="TopFront">TopFront</option>
                      <option value="TopRear">TopRear</option>
                      <option value="TopUp">TopUp</option>
                    </optgroup>
                    <optgroup label="3D">
                      <option value="Default">Default</option>
                      <option value="FrontRightQuarter">FrontRightQuarter</option>
                      <option value="FrontLeftQuarter">FrontLeftQuarter</option>
                      <option value="BackLeftQuarter">BackLeftQuarter</option>
                      <option value="BackRightQuarter">BackRightQuarter</option>
                    </optgroup>
                  </select>
                </div>

                {/* LIC + IsDelta */}
                <div className="wl-field">
                  <label className="wl-label">Options</label>
                  <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer' }}>
                      <input
                        id="wl-lic"
                        type="checkbox"
                        checked={isLICEnabled}
                        onChange={e => setIsLICEnabled(e.target.checked)}
                        style={{ width: '1rem', height: '1rem', accentColor: 'var(--mav-blue)', cursor: 'pointer' }}
                      />
                      <span className="wl-hint" style={{ margin: 0 }}>LIC (Line Integral Convolution)</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer' }}>
                      <input
                        id="wl-is-delta"
                        type="checkbox"
                        checked={isDelta}
                        onChange={e => setIsDelta(e.target.checked)}
                        style={{ width: '1rem', height: '1rem', accentColor: 'var(--mav-blue)', cursor: 'pointer' }}
                      />
                      <span className="wl-hint" style={{ margin: 0 }}>Is Delta</span>
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div className="wl-field wl-span2">
                  <div className="wl-actions">
                    <select
                      className="wl-mode-select"
                      value={outputMode}
                      onChange={e => setOutputMode(e.target.value as OutputMode)}>
                      <option value="launch">Launch — mav-launcher://</option>
                      <option value="direct">Direct — localhost:8342</option>
                      <option value="json">JSON — inspect only</option>
                    </select>
                    <button
                      type="button"
                      className="wl-btn-go"
                      onClick={handleGo}
                      title="Build output based on selected mode">
                      <span aria-hidden="true">🚀</span> Go
                    </button>
                  </div>
                  {statusMsg && <div className="wl-status">{statusMsg}</div>}
                </div>

              </div>
            </div>

            {/* INFO PANELS */}
            <div className="wl-panels">
              <details className="wl-panel" open>
                <summary>
                  <span className="wl-chevron">▶</span>
                  <span>Instructions</span>
                </summary>
                <div className="wl-panel-content">
                  <dl className="wl-dl">
                    <dt className="wl-dt">Install / Update</dt>
                    <dd className="wl-dd">
                      Install or update to the latest <strong>MAV-Aero</strong> release.
                    </dd>
                    <dt className="wl-dt">Register Protocol</dt>
                    <dd className="wl-dd">
                      Launch MAV-Aero manually once so the <code>mav-launcher://</code> protocol
                      handler is registered with the OS.
                    </dd>
                    <dt className="wl-dt">Launch Behavior</dt>
                    <dd className="wl-dd">
                      Generated links start MAV-Aero if it&apos;s not running; if already running
                      they import the selected cases into the current or a new session (per user
                      choice).
                    </dd>
                    <dt className="wl-dt">Troubleshooting</dt>
                    <dd className="wl-dd">
                      If a link does not open, reopen MAV-Aero manually then retry. Allow any
                      browser prompt to open the external application.
                    </dd>
                  </dl>
                </div>
              </details>

              <details className="wl-panel" open>
                <summary>
                  <span className="wl-chevron">▶</span>
                  <span>Output Mode</span>
                </summary>
                <div className="wl-panel-content">
                  <dl className="wl-dl">
                    <dt className="wl-dt">Launch</dt>
                    <dd className="wl-dd">
                      Encodes the JSON payload and navigates to{' '}
                      <code>mav-launcher://&#123;encoded&#125;</code>. Starts MAV-Aero if not
                      running, or imports into the current instance.
                    </dd>
                    <dt className="wl-dt">Direct</dt>
                    <dd className="wl-dd">
                      Posts to <code>http://localhost:8342/mav/import/&#123;encoded&#125;</code>.
                      Targets a running MAV-Aero instance or the Unity editor in Play mode.
                    </dd>
                    <dt className="wl-dt">JSON</dt>
                    <dd className="wl-dd">
                      No navigation — displays the generated JSON formatted, minified, and
                      URL-encoded for inspection or manual use.
                    </dd>
                  </dl>
                </div>
              </details>
            </div>

            {/* OUTPUT */}
            {outputItems.length > 0 && (
              <div className="wl-output-wrap">
                <hr className="wl-output-hr" />
                {outputItems.map(item => {
                  if (item.kind === 'launch' || item.kind === 'direct') {
                    return (
                      <div key={item.id} className="wl-gen-item">
                        <button
                          type="button"
                          className={item.kind === 'direct' ? 'wl-direct-btn' : 'wl-launch-btn'}
                          onClick={() => {
                            window.open(item.url, '_blank')
                          }}>
                          {item.label}
                        </button>
                        <code className="wl-url-code">{item.url}</code>
                        {renderCopyBtn(item.url, `url-${item.id}`)}
                      </div>
                    )
                  }

                  if (item.kind !== 'json') return null
                  const sections = [
                    { title: 'Formatted', text: item.formatted },
                    { title: 'Minified', text: item.minified },
                    { title: 'URL-encoded', text: item.urlEncoded }
                  ]
                  return (
                    <div key={item.id} className="wl-json-card">
                      {sections.map(({ title, text }) => (
                        <div key={title} className="wl-json-section">
                          <div className="wl-json-header">
                            <span>{title}</span>
                            {renderCopyBtn(text, `json-${item.id}-${title}`)}
                          </div>
                          <pre className="wl-json-pre">{text}</pre>
                        </div>
                      ))}
                    </div>
                  )
                })}
              </div>
            )}

          </div>
        </div>

        {/* FOOTER */}
        <footer className="mav-footer">
          <div>
            <div className="mav-footer-brand">MAV-Aero</div>
            <p style={{ marginTop: '4px' }}>© 2026 GM Motorsports. All rights reserved.</p>
          </div>
          <div className="mav-footer-links">
            <Link to="/mav-aero" style={{ textDecoration: 'none', fontSize: '.82rem', color: 'var(--mav-muted)' }}>
              MAV-Aero
            </Link>
            <Link to="/mav-aero-docs" style={{ textDecoration: 'none', fontSize: '.82rem', color: 'var(--mav-muted)' }}>
              Docs
            </Link>
          </div>
        </footer>
      </div>
    </>
  )
}

export default MAVAeroWebLauncher
